/**
 * JournalArticlesShell
 *
 * The outer frame shared by every journal articles page. It renders:
 *   - the left JournalArticlesSidebar (navigation, consistent on all pages),
 *   - optionally the standard Card scaffold (header with <h1> title,
 *     description, and optional header actions, plus a CardContent that fills
 *     the available height) — used by all table pages, and
 *   - an optional inline BiblioSourceDetailsPanel on the right that appears
 *     when an article is selected in the shared context.
 *
 * The Card scaffold renders when `description` is provided. When it is omitted,
 * children are rendered directly, which lets non-Card pages (e.g. the overview)
 * and placeholder pages compose their own layout inside the sidebar frame.
 *
 * Sidebar navigation is driven by the URL: the shell derives the selected
 * option from the current path and navigates on change, so the sidebar does not
 * depend on which page is mounted.
 */

import { ReactNode, useState, useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import JournalArticlesSidebar, {
  SidebarOption,
} from "@/components/journal-articles/JournalArticlesSidebar"
import SearchButton from "@/components/shared/SearchButton"
import BiblioSourceDetailsPanel from "@/components/shared/BiblioSourceDetailsPanel"
import {
  pathToSidebarOption,
  sidebarOptionToPath,
} from "@/components/journal-articles/sidebarRoutes"
import { collectionIdFromOption } from "@/components/journal-articles/JournalArticlesSidebar"
import { useJournalArticles } from "@/components/journal-articles/JournalArticlesContext"
import { JournalArticle } from "@/services/journalArticlesService"
import { useAuth } from "@/hooks/useAuth"
import { getParasiteRateSourceIds } from "@/services/parasiteRateService"

/**
 * Props for {@link JournalArticlesShell}.
 */
interface JournalArticlesShellProps {
  /**
   * The page body rendered between the sidebar and the (optional) details
   * panel. Optional so placeholder pages can render just the sidebar frame with
   * no body yet.
   */
  children?: ReactNode
  /**
   * The browser tab title and (when the Card scaffold renders) the <h1>
   * heading for this page. Rendered via React 19's automatic <title> hoisting.
   */
  title?: string
  /**
   * A short description of what the page shows. When provided, the shell wraps
   * children in the standard Card scaffold (Card + CardHeader with <h1> title,
   * description, and optional header actions + CardContent). When omitted,
   * children are rendered directly inside the sidebar frame, so non-Card pages
   * can compose their own layout.
   */
  description?: string
  /**
   * Optional actions rendered on the right of the Card header (e.g. a
   * collection action bar). Only relevant when `description` is provided and
   * the Card scaffold renders.
   */
  headerActions?: ReactNode
  /**
   * The current committed table search query. When provided together with
   * `onSearchChange`, the shell renders a prominent, always-open search input in
   * the top-right of the Card header, inline with the title/description. Omitted
   * on pages that do not use table search.
   */
  searchValue?: string
  /**
   * Handler called when the header search query is submitted (Enter or the
   * Search button). Required alongside `searchValue` for the header search to
   * render; wire it to the page's search setter.
   *
   * @param value - The newly submitted search query.
   */
  onSearchChange?: (value: string) => void
  /**
   * Whether to render the inline details panel when an article is selected.
   * Table pages set this true; non-table pages (which never select an article)
   * leave it false so no panel machinery is wired up. Defaults to false.
   */
  showDetailsPanel?: boolean
  /**
   * Called when the details panel reports an article update (e.g. after its
   * Manage Collections dialog changes memberships). Only relevant when
   * `showDetailsPanel` is true. Pages use this to keep the cached list and the
   * current view consistent.
   *
   * @param updated - The updated article from the panel.
   */
  onArticleUpdate?: (updated: JournalArticle) => void
}

/**
 * The shared outer frame for journal articles pages.
 *
 * @param props - The shell props.
 * @returns The rendered shell element.
 */
export default function JournalArticlesShell({
  children,
  title,
  description,
  headerActions,
  searchValue,
  onSearchChange,
  showDetailsPanel = false,
  onArticleUpdate,
}: JournalArticlesShellProps): React.JSX.Element {
  const location = useLocation()
  const navigate = useNavigate()
  const { getAccessToken } = useAuth()
  const {
    collections,
    collectionsLoading,
    selectedArticle,
    clearSelectedArticle,
    updateArticleInList,
  } = useJournalArticles()

  // The selected sidebar option is derived from the URL path so the sidebar
  // highlight is correct regardless of which page component is mounted.
  const selectedOption: SidebarOption = pathToSidebarOption(location.pathname)

  // The set of source IDs that have parasite rate data (latest version), used
  // by the details panel to surface a parasite-rate link. Loaded once; only
  // needed when the details panel can appear.
  const [parasiteRateSourceIds, setParasiteRateSourceIds] = useState<Set<string>>(new Set())
  useEffect(() => {
    if (!showDetailsPanel) {
      return
    }
    let cancelled: boolean = false
    async function fetchParasiteRateSourceIds(): Promise<void> {
      try {
        const token: string = await getAccessToken()
        const { sourceIds } = await getParasiteRateSourceIds(token)
        if (!cancelled) {
          setParasiteRateSourceIds(sourceIds)
        }
      } catch (err) {
        // The parasite-rate link is a convenience, not core data, so a failure
        // here is logged rather than surfaced as a page-level error.
        console.error("Failed to fetch parasite rate source IDs:", err)
      }
    }
    void fetchParasiteRateSourceIds()
    return () => {
      cancelled = true
    }
  }, [showDetailsPanel, getAccessToken])

  /**
   * Navigate to a new sidebar option by changing the URL path. The route change
   * mounts the corresponding page component.
   *
   * For collection options the destination path is type-specific (PR / RS /
   * user), so the collection's type is resolved from the loaded collections and
   * passed to the path builder. Resolving it here keeps the sidebar's
   * onOptionChange contract unchanged while still producing the correct path.
   *
   * @param option - The sidebar option to navigate to.
   */
  const handleSidebarOptionChange = (option: SidebarOption): void => {
    const collectionId: number | null = collectionIdFromOption(option)
    const collectionType: string | undefined =
      collectionId !== null
        ? collections.find((collection) => collection.id === collectionId)?.collectionType
        : undefined
    navigate(sidebarOptionToPath(option, collectionType), { replace: false })
  }

  /**
   * Handle an article update reported by the details panel. Always updates the
   * cached article, then delegates any page-specific follow-up (e.g. dropping a
   * row that no longer belongs in a collection view) to `onArticleUpdate`.
   *
   * @param updated - The updated article from the panel.
   */
  const handleArticleUpdateFromPanel = (updated: JournalArticle): void => {
    updateArticleInList(updated)
    onArticleUpdate?.(updated)
  }

  // When `description` is provided the shell renders the standard Card scaffold
  // around the children; otherwise children render directly so non-Card pages
  // can compose their own layout.
  const body: ReactNode =
    description !== undefined ? (
      <Card className="flex-1 min-w-0 flex flex-col min-h-0 shadow-none">
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <CardTitle>
                <h1>{title}</h1>
              </CardTitle>
              <CardDescription>{description}</CardDescription>
            </div>
            {(searchValue !== undefined && onSearchChange !== undefined) ||
            headerActions !== undefined ? (
              <div className="flex items-center gap-2 shrink-0">
                {/* Prominent, always-open search inline with the title. Rendered
                    only when the page wires up search state. */}
                {searchValue !== undefined && onSearchChange !== undefined && (
                  <SearchButton
                    value={searchValue}
                    onChange={onSearchChange}
                    placeholder="Search articles"
                    alwaysOpen
                  />
                )}
                {headerActions}
              </div>
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="flex flex-1 flex-col min-h-0">{children}</CardContent>
      </Card>
    ) : (
      children
    )

  return (
    <>
      {title !== undefined && <title>{title} - MAP Apps Platform</title>}
      <div className="flex flex-1 min-h-0">
        <JournalArticlesSidebar
          selectedOption={selectedOption}
          onOptionChange={handleSidebarOptionChange}
          collections={collections}
          collectionsLoading={collectionsLoading}
        />

        {body}

        {/* Inline details panel — appears on the right when an article is
            selected, only on pages that opt in. */}
        {showDetailsPanel && selectedArticle && (
          <BiblioSourceDetailsPanel
            article={selectedArticle}
            parasiteRateSourceIds={parasiteRateSourceIds}
            onClose={clearSelectedArticle}
            collections={collections}
            onArticleUpdate={handleArticleUpdateFromPanel}
          />
        )}
      </div>
    </>
  )
}

/**
 * Toast-and-redirect helper for pages that view a collection which turns out to
 * be inaccessible (denied access, or a deleted/private collection reached via a
 * shared link). Mirrors the old interface's forbidden-collection handling.
 *
 * This is exported for reuse by collection pages. It is a plain function rather
 * than a hook so a page can call it from an effect.
 *
 * @param navigate - The navigate function from useNavigate.
 */
export function redirectAwayFromForbiddenCollection(
  navigate: ReturnType<typeof useNavigate>
): void {
  toast.error("That collection isn't available.", { duration: 5000 })
  navigate(sidebarOptionToPath("all-journal-articles"), { replace: true })
}
